import { HotSingleWallet } from "@blaze-cardano/sdk";
import { getPublicKeyAsync, signAsync, utils } from "@noble/ed25519";

const STORAGE_KEY_PREFIX = "gummiworm:ephemeral-wallet:";

interface IEphemeralWalletRecord {
  privateKey: string;
  publicKey: string;
}

const getStorageKey = (mainAddress: string) =>
  `${STORAGE_KEY_PREFIX}${mainAddress}`;

const bytesToHex = (bytes: Uint8Array) => Buffer.from(bytes).toString("hex");

const readWalletRecord = (
  mainAddress: string,
): IEphemeralWalletRecord | undefined => {
  const serialized = window.localStorage.getItem(getStorageKey(mainAddress));
  if (!serialized) {
    return undefined;
  }

  const parsed = JSON.parse(serialized) as Partial<IEphemeralWalletRecord>;
  if (
    typeof parsed.privateKey !== "string" ||
    typeof parsed.publicKey !== "string"
  ) {
    return undefined;
  }

  return {
    privateKey: parsed.privateKey,
    publicKey: parsed.publicKey,
  };
};

const writeWalletRecord = (
  mainAddress: string,
  record: IEphemeralWalletRecord,
): IEphemeralWalletRecord => {
  window.localStorage.setItem(getStorageKey(mainAddress), JSON.stringify(record));
  return record;
};

export const ensureEphemeralWallet = async (
  mainAddress: string,
): Promise<IEphemeralWalletRecord> => {
  const existing = readWalletRecord(mainAddress);
  if (existing) {
    return existing;
  }

  const privateKey = utils.randomPrivateKey();
  const publicKey = await getPublicKeyAsync(privateKey);

  return writeWalletRecord(mainAddress, {
    privateKey: bytesToHex(privateKey),
    publicKey: bytesToHex(publicKey),
  });
};

export const signWithEphemeralWallet = async (
  provider: Provider,
  mainAddress: string,
  payload: Uint8Array,
) => {
  const network = window.__APP_CONFIG.env === "mainnet" ? "mainnet" : "preview";
  const wallet = await ensureEphemeralWallet(mainAddress);
  const hotSingle = new HotSingleWallet(
    wallet.privateKey,
    network,
    provider
  );

  return hotSingle.signData(hotSingle.address, payload.toString("hex"));
};
